import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import boqService, {
  BOQRequest,
  BOQHeader,
  LineItemRequest,
  ImportExcelRequest,
  ImportResult,
  MaterialRequest,
  OperationRequest,
  BOQLineItemMaterial,
  BOQLineItemOperation,
  CostBreakdown,
  BoqLineItemPricingHistoryRow
} from '../services/boq.service';
import { message } from 'antd';
import { documentKeys } from './useDocuments';
import { DOCUMENT_ENTITY_TYPE_BOQ_LINE_ITEM } from '@/constants/document-entity-types';

// Query keys
export const BOQ_KEYS = {
  all: ['boqs'] as const,
  lists: () => [...BOQ_KEYS.all, 'list'] as const,
  list: (filters?: any) => [...BOQ_KEYS.lists(), { filters }] as const,
  details: () => [...BOQ_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...BOQ_KEYS.details(), id] as const,
  materials: (boqId: string, lineItemId: string) => [...BOQ_KEYS.detail(boqId), 'materials', lineItemId] as const,
  operations: (boqId: string, lineItemId: string) => [...BOQ_KEYS.detail(boqId), 'operations', lineItemId] as const,
  costBreakdown: (boqId: string, lineItemId: string) => [...BOQ_KEYS.detail(boqId), 'cost-breakdown', lineItemId] as const,
  /** Past BOQ lines for "pricing from history" (search) */
  pricingHistory: (search: string, excludeLineItemId?: string) =>
    [...BOQ_KEYS.all, 'pricing-history', search, excludeLineItemId] as const,
};

export type { BoqLineItemPricingHistoryRow };

/**
 * Get all BOQs
 */
export function useBOQs(
  filters?: {
    customerId?: string;
    opportunity_id?: string;
    status?: string;
    fromDate?: string;
    toDate?: string;
    search?: string;
    sales_team_id?: string;
  },
  queryOptions?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: BOQ_KEYS.list(filters),
    queryFn: () => boqService.getAll(filters),
    staleTime: 30000, // 30 seconds
    enabled: queryOptions?.enabled ?? true,
  });
}

/**
 * Get BOQ by ID
 */
export function useBOQ(
  id: string,
  options?: Omit<UseQueryOptions<BOQHeader>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: BOQ_KEYS.detail(id),
    queryFn: async () => {
      const raw = (await boqService.getById(id)) as BOQHeader | { data: BOQHeader };
      return raw && typeof raw === 'object' && 'data' in raw && (raw as { data: BOQHeader }).data
        ? (raw as { data: BOQHeader }).data
        : (raw as BOQHeader);
    },
    enabled: !!id,
    ...options,
  });
}

/**
 * Create BOQ
 */
export function useCreateBOQ() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BOQRequest) => boqService.create(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.lists() });
      message.success('BOQ created successfully');
      return data;
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create BOQ');
    }
  });
}

/**
 * Update BOQ
 */
export function useUpdateBOQ() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<BOQRequest> }) =>
      boqService.update(id, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.detail(variables.id) });
      message.success('BOQ updated successfully');
      return data;
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update BOQ');
    }
  });
}

/**
 * Update BOQ status
 */
export function useUpdateBOQStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      boqService.updateStatus(id, status),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.detail(variables.id) });
      message.success(`BOQ status updated to ${variables.status} successfully`);
      return data;
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update BOQ status');
    }
  });
}

/**
 * Submit BOQ for review
 */
export function useSubmitBOQForReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => boqService.submitForReview(id),
    onSuccess: (data, id) => {
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.detail(id) });
      message.success('BOQ submitted for review successfully');
      return data;
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to submit BOQ for review');
    }
  });
}

/**
 * Approve BOQ
 */
export function useApproveBOQ() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => boqService.approve(id),
    onSuccess: (data, id) => {
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.detail(id) });
      message.success('BOQ approved successfully');
      return data;
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to approve BOQ');
    }
  });
}

/**
 * Reject BOQ
 */
export function useRejectBOQ() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      boqService.reject(id, reason),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.detail(variables.id) });
      message.success('BOQ rejected successfully');
      return data;
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to reject BOQ');
    }
  });
}

/**
 * Delete BOQ
 */
export function useDeleteBOQ() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => boqService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.lists() });
      message.success('BOQ deleted successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to delete BOQ');
    }
  });
}

/**
 * Add line items to BOQ
 */
export function useAddLineItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ boqId, items }: { boqId: string; items: LineItemRequest[] }) =>
      boqService.addLineItems(boqId, items),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.detail(variables.boqId) });
      message.success('Line items added successfully');
      return data;
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to add line items');
    }
  });
}

/**
 * Update line item
 */
export function useUpdateLineItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      boqId,
      itemId,
      data
    }: {
      boqId: string;
      itemId: string;
      data: Partial<LineItemRequest>;
    }) => boqService.updateLineItem(boqId, itemId, data),
    onSuccess: async (data, variables) => {
      // Auto-recalculate line item costs after updating
      await boqService.recalculateLineItem(variables.boqId, variables.itemId);
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.detail(variables.boqId) });
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.costBreakdown(variables.boqId, variables.itemId) });
      message.success('Line item updated and costs recalculated');
      return data;
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update line item');
    }
  });
}

/**
 * Delete line item
 */
export function useDeleteLineItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ boqId, itemId }: { boqId: string; itemId: string }) =>
      boqService.deleteLineItem(boqId, itemId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.detail(variables.boqId) });
      message.success('Line item deleted successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to delete line item');
    }
  });
}

/**
 * Recalculate BOQ totals
 */
export function useRecalculateBOQ() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (boqId: string) => boqService.recalculate(boqId),
    onSuccess: (data, boqId) => {
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.detail(boqId) });
      message.success('BOQ totals recalculated');
      return data;
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to recalculate BOQ');
    }
  });
}

/**
 * Duplicate BOQ
 */
export function useDuplicateBOQ() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (boqId: string) => boqService.duplicate(boqId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.lists() });
      message.success('BOQ duplicated successfully');
      return data;
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to duplicate BOQ');
    }
  });
}

/**
 * Import BOQ from Excel
 */
export function useImportBOQ() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: ImportExcelRequest) => boqService.importExcel(request),
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.lists() });
      // Result is the ImportResult directly: { success: true, boqId: ..., message: ..., statistics: {...} }
      if (result?.success) {
        message.success(result.message || 'BOQ imported successfully');
      } else {
        message.error(result?.message || 'Import completed with errors');
      }
      return result;
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to import BOQ');
    }
  });
}

/**
 * Export BOQ to Excel
 */
export function useExportBOQ() {
  return useMutation({
    mutationFn: async ({ boqId, filename }: { boqId: string; filename: string }) => {
      const blob = await boqService.exportExcel(boqId);
      boqService.downloadExcelFile(blob, filename);
      return blob;
    },
    onSuccess: () => {
      message.success('BOQ exported successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to export BOQ');
    }
  });
}

// ========================================
// Materials Management
// ========================================

/**
 * Get materials for a BOQ line item
 */
export function useBOQMaterials(boqId: string, lineItemId: string) {
  return useQuery({
    queryKey: BOQ_KEYS.materials(boqId, lineItemId),
    queryFn: () => boqService.getMaterials(boqId, lineItemId),
    enabled: !!boqId && !!lineItemId
  });
}

/**
 * Add material to BOQ line item
 */
export function useAddBOQMaterial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ boqId, lineItemId, material }: { boqId: string; lineItemId: string; material: MaterialRequest }) =>
      boqService.addMaterial(boqId, lineItemId, material),
    onSuccess: async (data, variables) => {
      // Auto-recalculate line item costs after adding material
      await boqService.recalculateLineItem(variables.boqId, variables.lineItemId);
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.materials(variables.boqId, variables.lineItemId) });
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.detail(variables.boqId) });
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.costBreakdown(variables.boqId, variables.lineItemId) });
      message.success('Material added and costs recalculated');
      return data;
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to add material');
    }
  });
}

/**
 * Update material
 */
export function useUpdateBOQMaterial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      boqId,
      lineItemId,
      materialId,
      material
    }: {
      boqId: string;
      lineItemId: string;
      materialId: string;
      material: Partial<MaterialRequest>;
    }) => boqService.updateMaterial(boqId, lineItemId, materialId, material),
    onSuccess: async (data, variables) => {
      // Auto-recalculate line item costs after updating material
      await boqService.recalculateLineItem(variables.boqId, variables.lineItemId);
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.materials(variables.boqId, variables.lineItemId) });
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.detail(variables.boqId) });
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.costBreakdown(variables.boqId, variables.lineItemId) });
      message.success('Material updated and costs recalculated');
      return data;
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update material');
    }
  });
}

/**
 * Delete material
 */
export function useDeleteBOQMaterial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ boqId, lineItemId, materialId }: { boqId: string; lineItemId: string; materialId: string }) =>
      boqService.deleteMaterial(boqId, lineItemId, materialId),
    onSuccess: async (data, variables) => {
      // Auto-recalculate line item costs after deleting material
      await boqService.recalculateLineItem(variables.boqId, variables.lineItemId);
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.materials(variables.boqId, variables.lineItemId) });
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.detail(variables.boqId) });
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.costBreakdown(variables.boqId, variables.lineItemId) });
      message.success('Material deleted and costs recalculated');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to delete material');
    }
  });
}

// ========================================
// Operations Management
// ========================================

/**
 * Get operations for a BOQ line item
 */
export function useBOQOperations(boqId: string, lineItemId: string) {
  return useQuery({
    queryKey: BOQ_KEYS.operations(boqId, lineItemId),
    queryFn: () => boqService.getOperations(boqId, lineItemId),
    enabled: !!boqId && !!lineItemId
  });
}

/**
 * Add operation to BOQ line item
 */
export function useAddBOQOperation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      boqId,
      lineItemId,
      operation
    }: {
      boqId: string;
      lineItemId: string;
      operation: OperationRequest;
    }) => {
      const data = await boqService.addOperation(boqId, lineItemId, operation);
      await boqService.recalculateLineItem(boqId, lineItemId);
      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.operations(variables.boqId, variables.lineItemId) });
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.detail(variables.boqId) });
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.costBreakdown(variables.boqId, variables.lineItemId) });
      message.success('Operation added and costs recalculated');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to add operation');
    }
  });
}

/**
 * Update operation
 */
export function useUpdateBOQOperation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      boqId,
      lineItemId,
      operationId,
      operation
    }: {
      boqId: string;
      lineItemId: string;
      operationId: string;
      operation: Partial<OperationRequest>;
    }) => {
      const data = await boqService.updateOperation(boqId, lineItemId, operationId, operation);
      await boqService.recalculateLineItem(boqId, lineItemId);
      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.operations(variables.boqId, variables.lineItemId) });
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.detail(variables.boqId) });
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.costBreakdown(variables.boqId, variables.lineItemId) });
      message.success('Operation updated and costs recalculated');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update operation');
    }
  });
}

/**
 * Delete operation
 */
export function useDeleteBOQOperation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ boqId, lineItemId, operationId }: { boqId: string; lineItemId: string; operationId: string }) =>
      boqService.deleteOperation(boqId, lineItemId, operationId),
    onSuccess: async (data, variables) => {
      // Auto-recalculate line item costs after deleting operation
      await boqService.recalculateLineItem(variables.boqId, variables.lineItemId);
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.operations(variables.boqId, variables.lineItemId) });
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.detail(variables.boqId) });
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.costBreakdown(variables.boqId, variables.lineItemId) });
      message.success('Operation deleted and costs recalculated');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to delete operation');
    }
  });
}

// ========================================
// Cost Calculation
// ========================================

/**
 * Get cost breakdown for a line item
 */
export function useBOQCostBreakdown(boqId: string, lineItemId: string) {
  return useQuery({
    queryKey: BOQ_KEYS.costBreakdown(boqId, lineItemId),
    queryFn: () => boqService.getCostBreakdown(boqId, lineItemId),
    enabled: !!boqId && !!lineItemId
  });
}

/**
 * Recalculate line item costs
 */
export function useRecalculateBOQLineItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ boqId, lineItemId }: { boqId: string; lineItemId: string }) =>
      boqService.recalculateLineItem(boqId, lineItemId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.detail(variables.boqId) });
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.costBreakdown(variables.boqId, variables.lineItemId) });
      message.success('Line item costs recalculated');
      return data;
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to recalculate line item');
    }
  });
}

/**
 * Toggle manual cost override
 */
export function useToggleBOQManualCost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ boqId, lineItemId, useManual }: { boqId: string; lineItemId: string; useManual: boolean }) =>
      boqService.toggleManualCost(boqId, lineItemId, useManual),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.detail(variables.boqId) });
      message.success('Cost mode updated successfully');
      return data;
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to toggle cost mode');
    }
  });
}

/**
 * Update line item margin
 */
export function useUpdateBOQLineItemMargin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      boqId,
      lineItemId,
      marginPercentage
    }: {
      boqId: string;
      lineItemId: string;
      marginPercentage: number | null;
    }) => boqService.updateLineItemMargin(boqId, lineItemId, marginPercentage),
    onSuccess: (data, variables) => {
      // Backend updateLineItemMargin already runs calculateLineItemCost; only refresh caches
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.detail(variables.boqId) });
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.costBreakdown(variables.boqId, variables.lineItemId) });
      message.success('Line item margin updated and costs recalculated');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update line item margin');
    }
  });
}

/**
 * Search other BOQ line items in the company (pricing history) — enabled when `search` has at least 2 characters.
 */
export function useBoqLineItemPricingHistory(
  search: string,
  options?: { excludeLineItemId?: string; limit?: number; enabled?: boolean }
) {
  const q = search.trim();
  return useQuery<BoqLineItemPricingHistoryRow[]>({
    queryKey: BOQ_KEYS.pricingHistory(q, options?.excludeLineItemId),
    queryFn: () =>
      boqService.searchLineItemPricingHistory({
        search: q,
        excludeLineItemId: options?.excludeLineItemId,
        limit: options?.limit
      }),
    enabled: (options?.enabled ?? true) && q.length >= 2
  });
}

/**
 * Copy materials, operations, line cost fields, and technical documents from a source line (server-side).
 */
export function useCopyBoqLineItemPricing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { boqId: string; lineItemId: string; sourceLineItemId: string }) =>
      boqService.copyLineItemPricingFromSource(vars.boqId, vars.lineItemId, vars.sourceLineItemId),
    onSuccess: (data, variables) => {
      void data;
      queryClient.invalidateQueries({ queryKey: BOQ_KEYS.detail(variables.boqId) });
      if (variables.lineItemId) {
        queryClient.invalidateQueries({ queryKey: BOQ_KEYS.materials(variables.boqId, variables.lineItemId) });
        queryClient.invalidateQueries({ queryKey: BOQ_KEYS.operations(variables.boqId, variables.lineItemId) });
        queryClient.invalidateQueries({ queryKey: BOQ_KEYS.costBreakdown(variables.boqId, variables.lineItemId) });
        queryClient.invalidateQueries({
          queryKey: ['boq-line-attachments', variables.boqId],
        });
        queryClient.invalidateQueries({
          queryKey: documentKeys.byEntity(DOCUMENT_ENTITY_TYPE_BOQ_LINE_ITEM, variables.lineItemId),
        });
      }
    }
  });
}

